import React, { useCallback, useEffect, useState } from 'react';

import { MenuItem, ModalBox, Table } from 'components';
import { Edit2 } from 'iconsax-react';

import { useSelector, useTranslation } from 'third-party';

import { useUser } from 'modules/user';

import { userSelectors } from 'selectors';

import { FilterValue } from 'components/Table/types';

import { userColumns } from 'features/Users/MembersColumns';
import { UserRightsEditForm } from 'features/Users/UserRightsEditForm';

import { APIUser } from 'types/api';

import { palette } from 'styles/theme';

type Props = {
  organizationId: string;
};

export const OrganizationUsersTab: React.FC<Props> = ({ organizationId }) => {
  const { t } = useTranslation();
  // loadUsers is bound to the super admin's own selected organization, while this
  // tab has to list the users of the organization taken from the route.
  const {
    clearUserFilters,
    clearUserList,
    loadUsersForOrganization,
    updateUserFilters,
  } = useUser();

  const [userTableColumns, setUserTableColumns] = useState(
    userColumns(organizationId),
  );
  const [userFiltersReady, setUserFiltersReady] = useState(false);
  const [selectedItem, setSelectedItem] = useState<APIUser | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const users = useSelector(userSelectors.userList);
  const userFilters = useSelector(userSelectors.userFilters);
  const usersPending = useSelector(userSelectors.loadUsersPending);
  const usersLoaded = useSelector(userSelectors.usersListLoaded);
  const allUsersLoaded = useSelector(userSelectors.allUsersLoaded);

  useEffect(() => {
    clearUserFilters();
    setUserFiltersReady(true);
    return () => {
      clearUserList();
      clearUserFilters();
      setUserFiltersReady(false);
    };
  }, [organizationId, clearUserFilters, clearUserList]);

  useEffect(() => {
    if (organizationId && userFiltersReady) {
      loadUsersForOrganization(organizationId);
    }
  }, [organizationId, userFiltersReady, userFilters, loadUsersForOrganization]);

  const onUserColumnFilterChange = useCallback(
    (columnId: string, value: string) => {
      setUserTableColumns(prev =>
        prev.map(col =>
          col.dataId === columnId ? { ...col, filter: value } : col,
        ),
      );
      updateUserFilters({ ...userFilters, [columnId]: value });
    },
    [userFilters, updateUserFilters],
  );

  const onUserAdvancedFilterChange = useCallback(
    (columnId: string, value: FilterValue) => {
      setUserTableColumns(prev =>
        prev.map(col =>
          col.dataId === columnId ? { ...col, filterValue: value } : col,
        ),
      );
      updateUserFilters({ ...userFilters, [columnId]: value });
    },
    [userFilters, updateUserFilters],
  );

  const onUserFilterReset = useCallback(() => {
    clearUserFilters();
    setUserTableColumns(prev =>
      prev.map(col => ({ ...col, filter: undefined, filterValue: undefined })),
    );
  }, [clearUserFilters]);

  const onUserColumnVisibilityChanged = useCallback(
    (columnId: string, value: boolean) => {
      setUserTableColumns(prev =>
        prev.map(col =>
          col.dataId === columnId ? { ...col, hidden: !value } : col,
        ),
      );
    },
    [],
  );

  const rowOptionsMenu = useCallback(
    (item: APIUser, onRowMenuClose: () => void): React.ReactElement[] => [
      <MenuItem
        key="edit"
        onClick={() => {
          setIsEditModalOpen(true);
          setSelectedItem(item);
          onRowMenuClose();
        }}
      >
        <Edit2
          variant="TwoTone"
          color={palette.primary900}
          size="24"
          style={{ paddingRight: '0.5rem' }}
        />
        {t('table.edit')}
      </MenuItem>,
    ],
    [t],
  );

  const onEditModalClose = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedItem(null);
  }, []);

  return (
    <>
      <Table<APIUser>
        header={t('membersList.header')}
        subHeader={t('membersList.subHeader')}
        filterable
        columns={userTableColumns}
        items={users ?? []}
        allItemsLoaded={allUsersLoaded}
        itemsLoaded={usersLoaded}
        loadItemsPending={usersPending}
        rowOptionsMenu={rowOptionsMenu}
        onColumnFilterChange={onUserColumnFilterChange}
        onAdvancedFilterChange={onUserAdvancedFilterChange}
        onFilterReset={onUserFilterReset}
        onColumnVisibilityChanged={onUserColumnVisibilityChanged}
        onColumnSizeChanged={(id, width) => {
          setUserTableColumns(prev =>
            prev.map(col => (col.dataId === id ? { ...col, width } : col)),
          );
        }}
        bodyRowHeight="1rem"
        headerRowHeight="0.2rem"
        tableMinHeight="20rem"
        emptyText={t('superAdmin.users.empty')}
      />
      {isEditModalOpen && (
        <ModalBox width="30rem" height="19rem" onClose={onEditModalClose}>
          <UserRightsEditForm
            organizationId={organizationId}
            onModalClose={onEditModalClose}
            userId={selectedItem?.id}
          />
        </ModalBox>
      )}
    </>
  );
};
